import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von11Congenital } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MalformationCodeAutocomplete } from '@/components/common/MalformationCodeAutocomplete';

interface Von11CongenitalFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von11CongenitalForm: React.FC<Von11CongenitalFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);

  const [hasCongenAnomaly, setHasCongenAnomaly] = useState('');
  const [congenAnomaly, setCongenAnomaly] = useState<string>('');
  const [icd10QCodes, setIcd10QCodes] = useState<string>('');
  const [icd10QOther, setIcd10QOther] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getCongenital(patientId);

      if (data) {
        // Check if data has ID (existing record) or is auto-filled from patient
        const isExisting = Boolean(data.id);
        setHasExistingData(isExisting);

        // Track auto-filled fields
        if (data._autofilled && Array.isArray(data._autofilled)) {
          setAutoFilledFields(data._autofilled);
        } else {
          setAutoFilledFields([]);
        }

        setHasCongenAnomaly(data.has_congen_anomaly == null ? '' : String(Number(data.has_congen_anomaly)));
        setCongenAnomaly(data.congen_anomaly || '');
        setIcd10QCodes(data.icd10_q_codes || '');
        setIcd10QOther(data.icd10_q_other || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von11Congenital> = {
        patient_id: patientId,
        has_congen_anomaly: hasCongenAnomaly === '' ? undefined : hasCongenAnomaly === '1',
        // Only include fields that are NOT auto-filled from patient data
        congen_anomaly: autoFilledFields.includes('congen_anomaly') ? undefined : (congenAnomaly || undefined),
        icd10_q_codes: autoFilledFields.includes('icd10_q_codes') ? undefined : (icd10QCodes || undefined),
        icd10_q_other: icd10QOther || undefined
      };

      if (hasExistingData) {
        await vonDataService.updateCongenital(patientId, formData);
      } else {
        await vonDataService.createCongenital(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 11: Malformații congenitale</CardTitle>
        <CardDescription>Date despre anomalii congenitale și coduri ICD-10</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Congenital anomalies */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Anomalii congenitale</h3>

            <div className="space-y-2">
              <Label htmlFor="hasCongenAnomaly">Există?</Label>
              <Select value={hasCongenAnomaly} onValueChange={setHasCongenAnomaly} disabled={readOnly}>
                <SelectTrigger id="hasCongenAnomaly">
                  <SelectValue placeholder="Selectează..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Da</SelectItem>
                  <SelectItem value="0">Nu</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="congenAnomaly" className="flex items-center gap-2">
                Descriere anomalie congenitală
                {autoFilledFields.includes('congen_anomaly') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completat automat din date pacient</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Label>
              <Textarea
                id="congenAnomaly"
                value={congenAnomaly}
                onChange={(e) => setCongenAnomaly(e.target.value)}
                placeholder="Descrierea detaliată a anomaliilor congenitale"
                rows={4}
                disabled={readOnly || autoFilledFields.includes('congen_anomaly')}
                className={autoFilledFields.includes('congen_anomaly') ? 'border-blue-400 bg-blue-50' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="icd10QCodes" className="flex items-center gap-2">
                Coduri ICD-10 Q00-Q99 (separate prin virgulă)
                {autoFilledFields.includes('icd10_q_codes') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completat automat din date pacient</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Label>
              <MalformationCodeAutocomplete
                value={icd10QCodes}
                onChange={setIcd10QCodes}
                isDisabled={readOnly || autoFilledFields.includes('icd10_q_codes')}
                className={autoFilledFields.includes('icd10_q_codes') ? 'border-blue-400 rounded-md bg-blue-50 p-2' : ''}
              />
              <p className="text-xs text-gray-500">
                Căutați și selectați codurile ICD-10 pentru malformații congenitale (Q00-Q99)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="icd10QOther">Alte coduri ICD-10 Q (text)</Label>
              <Textarea
                id="icd10QOther"
                value={icd10QOther}
                onChange={(e) => setIcd10QOther(e.target.value)}
                placeholder="Coduri adiționale sau note"
                rows={3}
                disabled={readOnly}
              />
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
